import React, { useState, useEffect, useMemo } from 'react'
import {
    Card, Table, Button, Modal, Form, Input, Select,
    Progress, Tag, Space, message, Tooltip, Switch,
    InputNumber, List, Checkbox, Badge, Empty, Spin,
    Tabs, Upload, Alert
} from 'antd'
import {
    SendOutlined, PauseOutlined, StopOutlined,
    PlayCircleOutlined, PlusOutlined, DeleteOutlined,
    ReloadOutlined, EyeOutlined, UserOutlined,
    UploadOutlined, SearchOutlined, TeamOutlined, EditOutlined
} from '@ant-design/icons'

const { TextArea } = Input
const { Option } = Select

interface Broadcast {
    id: number
    name: string
    message_text: string
    status: string
    mode: string
    default_account_id?: number
    min_delay_seconds: number
    max_delay_seconds: number
    daily_limit_per_account: number
    total_recipients: number
    sent_count: number
    failed_count: number
    scheduled_at?: string
    started_at?: string
    completed_at?: string
    created_at: string
}

interface Recipient {
    id: number
    tg_chat_id?: number
    username?: string
    contact_name?: string
    status: string
    error_message?: string
    sent_at?: string
}

interface Account {
    id: number
    phone: string
    status: string
}

interface Dialog {
    chat_id: number
    first_name?: string
    last_name?: string
    username?: string
}

interface Contact {
    id: number
    tg_user_id: number
    username?: string
    first_name?: string
    last_name?: string
    phone?: string
    tags?: string[]
}

const API_BASE = '/api'

const Broadcasts: React.FC = () => {
    const [broadcasts, setBroadcasts] = useState<Broadcast[]>([])
    const [loading, setLoading] = useState(false)
    const [createModalVisible, setCreateModalVisible] = useState(false)
    const [recipientsModalVisible, setRecipientsModalVisible] = useState(false)
    const [selectChatsModalVisible, setSelectChatsModalVisible] = useState(false)
    const [selectedBroadcast, setSelectedBroadcast] = useState<Broadcast | null>(null)
    const [recipients, setRecipients] = useState<Recipient[]>([])
    const [accounts, setAccounts] = useState<Account[]>([])
    const [dialogs, setDialogs] = useState<Dialog[]>([])
    const [selectedDialogs, setSelectedDialogs] = useState<number[]>([])
    const [selectedAccounts, setSelectedAccounts] = useState<number[]>([])
    const [sendMode, setSendMode] = useState<string>('single_account')
    const [editingBroadcastId, setEditingBroadcastId] = useState<number | null>(null)
    const [dialogsLoading, setDialogsLoading] = useState(false)
    const [stats, setStats] = useState<any>(null)
    const [form] = Form.useForm()

    // Enhanced recipient selection
    const [recipientTab, setRecipientTab] = useState<string>('chats')
    const [searchQuery, setSearchQuery] = useState('')
    const [textareaInput, setTextareaInput] = useState('')
    const [parsedFromText, setParsedFromText] = useState<{ username?: string, tg_id?: number, display: string }[]>([])
    const [contacts, setContacts] = useState<Contact[]>([])
    const [selectedContacts, setSelectedContacts] = useState<number[]>([])
    const [contactsLoading, setContactsLoading] = useState(false)
    const [csvData, setCsvData] = useState<any[]>([])

    const token = localStorage.getItem('token')
    const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    }

    // Fetch broadcasts
    const fetchBroadcasts = async () => {
        setLoading(true)
        try {
            const res = await fetch(`${API_BASE}/broadcasts`, { headers })
            if (res.ok) {
                const data = await res.json()
                setBroadcasts(data)
            }
        } catch (e) {
            message.error('Ошибка загрузки рассылок')
        } finally {
            setLoading(false)
        }
    }

    // Fetch accounts
    const fetchAccounts = async () => {
        try {
            const res = await fetch(`${API_BASE}/accounts`, { headers })
            if (res.ok) {
                const data = await res.json()
                setAccounts(data.filter((a: Account) => a.status === 'active'))
            }
        } catch (e) {
            console.error('Error fetching accounts:', e)
        }
    }

    useEffect(() => {
        fetchBroadcasts()
        fetchAccounts()
    }, [])

    // Auto-refresh stats for running broadcasts
    useEffect(() => {
        const runningBroadcast = broadcasts.find(b => b.status === 'running')
        if (!runningBroadcast) return

        const interval = setInterval(async () => {
            try {
                const res = await fetch(`${API_BASE}/broadcasts/${runningBroadcast.id}/stats`, { headers })
                if (res.ok) {
                    const data = await res.json()
                    setStats(data)
                    // Update the running broadcast in the list without triggering a full loading state
                    setBroadcasts(prev => prev.map(b =>
                        b.id === data.id ? {
                            ...b,
                            status: data.status,
                            sent_count: data.sent_count,
                            failed_count: data.failed_count,
                            completed_at: data.completed_at
                        } : b
                    ))
                }
            } catch (e) {
                console.error('Error fetching stats:', e)
            }
        }, 3000)

        return () => clearInterval(interval)
    }, [broadcasts])

    // Create broadcast
    const handleCreate = async (values: any) => {
        try {
            // Collect recipients based on active tab
            let recipients: { tg_chat_id?: number, username?: string, contact_name?: string }[] = []

            switch (recipientTab) {
                case 'chats':
                    recipients = selectedDialogs.map(chatId => {
                        const dialog = dialogs.find(d => d.chat_id === chatId)
                        return {
                            tg_chat_id: chatId,
                            contact_name: dialog ? `${dialog.first_name || ''} ${dialog.last_name || ''}`.trim() : undefined
                        }
                    })
                    break
                case 'text':
                    recipients = parsedFromText.map(p => ({
                        tg_chat_id: p.tg_id,
                        username: p.username,
                        contact_name: p.username ? `@${p.username}` : `ID: ${p.tg_id}`
                    }))
                    break
                case 'csv':
                    recipients = csvData.map(row => ({
                        tg_chat_id: row.tg_id || undefined,
                        username: row.username || undefined,
                        contact_name: row.name || row.username || row.phone || undefined
                    }))
                    break
                case 'contacts':
                    recipients = selectedContacts.map(tgUserId => {
                        const contact = contacts.find(c => c.tg_user_id === tgUserId)
                        return {
                            tg_chat_id: tgUserId,
                            contact_name: contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : undefined
                        }
                    })
                    break
            }

            if (recipients.length === 0 && !editingBroadcastId) { // Allow editing without changing recipients
                message.warning('Выберите хотя бы одного получателя')
                return
            }

            const payload = {
                ...values,
                recipients: editingBroadcastId ? undefined : recipients, // Only send recipients on create
                account_ids: sendMode === 'multi_account' ? selectedAccounts : undefined
            }

            const url = editingBroadcastId
                ? `${API_BASE}/broadcasts/${editingBroadcastId}`
                : `${API_BASE}/broadcasts`

            const method = editingBroadcastId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(payload)
            })

            if (res.ok) {
                message.success(editingBroadcastId ? 'Рассылка обновлена' : `Рассылка создана (${recipients.length} получателей)`)
                setCreateModalVisible(false)
                // Reset all states
                setEditingBroadcastId(null)
                setSelectedDialogs([])
                setSelectedAccounts([])
                setParsedFromText([])
                setTextareaInput('')
                setCsvData([])
                setSelectedContacts([])
                setRecipientTab('chats')
                setSendMode('single_account')
                form.resetFields()
                fetchBroadcasts()
            } else {
                const error = await res.json()
                message.error(error.detail || (editingBroadcastId ? 'Ошибка обновления' : 'Ошибка создания'))
            }
        } catch (e) {
            message.error(editingBroadcastId ? 'Ошибка обновления рассылки' : 'Ошибка создания рассылки')
        }
    }

    const openCreateModal = () => {
        setEditingBroadcastId(null)
        form.resetFields()
        setSelectedDialogs([])
        setSelectedAccounts([])
        setParsedFromText([])
        setTextareaInput('')
        setCsvData([])
        setSelectedContacts([])
        setRecipientTab('chats')
        setSendMode('single_account')
        setCreateModalVisible(true)
    }

    const openEditModal = async (broadcast: Broadcast) => {
        setEditingBroadcastId(broadcast.id)

        // Pre-fill form
        form.setFieldsValue({
            name: broadcast.name,
            message_text: broadcast.message_text,
            mode: broadcast.mode,
            default_account_id: broadcast.default_account_id,
            min_delay_seconds: broadcast.min_delay_seconds,
            max_delay_seconds: broadcast.max_delay_seconds,
            daily_limit_per_account: broadcast.daily_limit_per_account,
        })

        setSendMode(broadcast.mode)

        // Fetch full broadcast details to get account_ids if multi_account (or we can just wait for recipients fetch)
        // Wait, broadcast might not have account_ids directly in the list view, or it does.
        // Actually, we'll fetch recipients to prepopulate the list.
        try {
            const res = await fetch(`${API_BASE}/broadcasts/${broadcast.id}/recipients`, { headers })
            if (res.ok) {
                const data: Recipient[] = await res.json()

                // Deduce account_ids from recipients if missing, or we could fetch the single broadcast endpoint
                // Let's fetch the single broadcast to get exact account_ids
                const bRes = await fetch(`${API_BASE}/broadcasts/${broadcast.id}`, { headers })
                if (bRes.ok) {
                    const bData = await bRes.json()
                    // Set selected accounts
                    if (bData.mode === 'multi_account' && bData.account_ids) {
                        setSelectedAccounts(bData.account_ids)
                    }
                }

                // Populate recipients logic: We will just drop them into the "text" tab for simplicity of editing,
                // or if they have tg_chat_id, maybe we try to match them.
                // The easiest way is to put them all in "text" format so user sees them as @username or ID format
                const textLines = data.map(r => r.username ? `@${r.username}` : (r.tg_chat_id ? `${r.tg_chat_id}` : '')).filter(Boolean)
                setTextareaInput(textLines.join('\n'))
                setRecipientTab('text')
                parseTextInput(textLines.join('\n'))

            }
        } catch (e) {
            message.error('Ошибка загрузки данных рассылки')
        }

        setCreateModalVisible(true)
    }

    // Start broadcast
    const handleStart = async (id: number) => {
        try {
            const res = await fetch(`${API_BASE}/broadcasts/${id}/start`, {
                method: 'POST',
                headers
            })
            if (res.ok) {
                message.success('Рассылка запущена')
                fetchBroadcasts()
            } else {
                const error = await res.json()
                message.error(error.detail || 'Ошибка запуска')
            }
        } catch (e) {
            message.error('Ошибка запуска рассылки')
        }
    }

    // Pause broadcast
    const handlePause = async (id: number) => {
        try {
            const res = await fetch(`${API_BASE}/broadcasts/${id}/pause`, {
                method: 'POST',
                headers
            })
            if (res.ok) {
                message.success('Рассылка приостановлена')
                fetchBroadcasts()
            } else {
                const error = await res.json()
                message.error(error.detail || 'Ошибка паузы')
            }
        } catch (e) {
            message.error('Ошибка паузы')
        }
    }

    // Stop broadcast
    const handleStop = async (id: number) => {
        Modal.confirm({
            title: 'Остановить рассылку?',
            content: 'Оставшиеся получатели будут пропущены. Продолжить?',
            okText: 'Да, остановить',
            cancelText: 'Отмена',
            onOk: async () => {
                try {
                    const res = await fetch(`${API_BASE}/broadcasts/${id}/stop`, {
                        method: 'POST',
                        headers
                    })
                    if (res.ok) {
                        message.success('Рассылка остановлена')
                        fetchBroadcasts()
                    } else {
                        const error = await res.json()
                        message.error(error.detail || 'Ошибка остановки')
                    }
                } catch (e) {
                    message.error('Ошибка остановки')
                }
            }
        })
    }

    // Delete broadcast
    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: 'Удалить рассылку?',
            content: 'Это действие нельзя отменить.',
            okText: 'Удалить',
            okButtonProps: { danger: true },
            cancelText: 'Отмена',
            onOk: async () => {
                try {
                    const res = await fetch(`${API_BASE}/broadcasts/${id}`, {
                        method: 'DELETE',
                        headers
                    })
                    if (res.ok) {
                        message.success('Рассылка удалена')
                        fetchBroadcasts()
                    }
                } catch (e) {
                    message.error('Ошибка удаления')
                }
            }
        })
    }

    // View recipients
    const viewRecipients = async (broadcast: Broadcast) => {
        setSelectedBroadcast(broadcast)
        try {
            const res = await fetch(`${API_BASE}/broadcasts/${broadcast.id}/recipients`, { headers })
            if (res.ok) {
                const data = await res.json()
                setRecipients(data)
                setRecipientsModalVisible(true)
            }
        } catch (e) {
            message.error('Ошибка загрузки получателей')
        }
    }

    // Load dialogs for selection
    const loadDialogs = async (accountId: number) => {
        setDialogsLoading(true)
        try {
            const res = await fetch(`${API_BASE}/accounts/${accountId}/dialogs`, { headers })
            if (res.ok) {
                const data = await res.json()
                setDialogs(data.dialogs || [])
            }
        } catch (e) {
            message.error('Ошибка загрузки диалогов')
        } finally {
            setDialogsLoading(false)
        }
    }

    // Fetch contacts for "All contacts" tab
    const fetchContacts = async () => {
        setContactsLoading(true)
        try {
            const res = await fetch(`${API_BASE}/contacts`, { headers })
            if (res.ok) {
                const data = await res.json()
                setContacts(data)
            }
        } catch (e) {
            message.error('Ошибка загрузки контактов')
        } finally {
            setContactsLoading(false)
        }
    }

    // Parse textarea input (usernames/IDs)
    const parseTextInput = (text: string) => {
        const lines = text.split('\n').filter(line => line.trim())
        const parsed: { username?: string, tg_id?: number, display: string }[] = []

        for (const line of lines) {
            const clean = line.trim()
            if (!clean) continue

            // Check if it's a number (tg_id)
            if (/^\d+$/.test(clean)) {
                parsed.push({ tg_id: parseInt(clean), display: `ID: ${clean}` })
            } else {
                // Username (with or without @)
                const username = clean.replace(/^@/, '')
                parsed.push({ username, display: `@${username}` })
            }
        }

        setParsedFromText(parsed)
        return parsed
    }

    // Handle CSV upload
    const handleCsvUpload = (file: File) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const text = e.target?.result as string
            const lines = text.split('\n').filter(l => l.trim())
            if (lines.length === 0) return

            // Parse header
            const header = lines[0].toLowerCase().split(/[,;\t]/)
            const data: any[] = []

            for (let i = 1; i < lines.length; i++) {
                const values = lines[i].split(/[,;\t]/)
                const row: any = {}
                header.forEach((h, idx) => {
                    const key = h.trim()
                    if (key.includes('username') || key.includes('user')) row.username = values[idx]?.trim()?.replace('@', '')
                    if (key.includes('id') || key.includes('tg_id')) row.tg_id = parseInt(values[idx]?.trim()) || null
                    if (key.includes('phone') || key.includes('телефон')) row.phone = values[idx]?.trim()
                    if (key.includes('name') || key.includes('имя')) row.name = values[idx]?.trim()
                })
                if (row.username || row.tg_id || row.phone) {
                    data.push(row)
                }
            }

            setCsvData(data)
            message.success(`Загружено ${data.length} контактов`)
        }
        reader.readAsText(file)
        return false // Prevent upload
    }

    // Filtered dialogs for search  
    const filteredDialogs = useMemo(() => {
        if (!searchQuery) return dialogs
        const q = searchQuery.toLowerCase()
        return dialogs.filter(d =>
            d.first_name?.toLowerCase().includes(q) ||
            d.last_name?.toLowerCase().includes(q) ||
            d.username?.toLowerCase().includes(q)
        )
    }, [dialogs, searchQuery])

    // Get total recipients count
    const getTotalRecipientsCount = () => {
        switch (recipientTab) {
            case 'chats': return selectedDialogs.length
            case 'text': return parsedFromText.length
            case 'csv': return csvData.length
            case 'contacts': return selectedContacts.length
            default: return 0
        }
    }

    const getStatusTag = (status: string) => {
        const colors: Record<string, string> = {
            draft: 'default',
            scheduled: 'blue',
            running: 'processing',
            paused: 'warning',
            completed: 'success',
            failed: 'error'
        }
        const labels: Record<string, string> = {
            draft: 'Черновик',
            scheduled: 'Запланировано',
            running: 'Выполняется',
            paused: 'Пауза',
            completed: 'Завершено',
            failed: 'Ошибка'
        }
        return <Tag color={colors[status]}>{labels[status] || status}</Tag>
    }

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: Broadcast) => (
                <div>
                    <strong>{name}</strong>
                    <br />
                    <small style={{ color: '#666' }}>
                        {record.message_text.substring(0, 50)}
                        {record.message_text.length > 50 ? '...' : ''}
                    </small>
                </div>
            )
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: getStatusTag
        },
        {
            title: 'Прогресс',
            key: 'progress',
            render: (_: any, record: Broadcast) => {
                const total = record.total_recipients || 1
                const done = record.sent_count + record.failed_count
                const percent = Math.round((done / total) * 100)
                return (
                    <div style={{ width: 150 }}>
                        <Progress
                            percent={percent}
                            size="small"
                            status={record.status === 'failed' ? 'exception' : undefined}
                        />
                        <small>{record.sent_count} / {record.total_recipients}</small>
                        {record.failed_count > 0 && (
                            <small style={{ color: '#ff4d4f', marginLeft: 8 }}>
                                ({record.failed_count} ошибок)
                            </small>
                        )}
                    </div>
                )
            }
        },
        {
            title: 'Режим',
            dataIndex: 'mode',
            key: 'mode',
            render: (mode: string) => (
                mode === 'multi_account' ? 'Несколько аккаунтов' : 'Один аккаунт'
            )
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: Broadcast) => (
                <Space>
                    {record.status === 'draft' && (
                        <Tooltip title="Запустить">
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={() => handleStart(record.id)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'draft' && (
                        <Tooltip title="Редактировать">
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => openEditModal(record)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'paused' && (
                        <Tooltip title="Продолжить">
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={() => handleStart(record.id)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'running' && (
                        <>
                            <Tooltip title="Пауза">
                                <Button
                                    icon={<PauseOutlined />}
                                    onClick={() => handlePause(record.id)}
                                />
                            </Tooltip>
                            <Tooltip title="Остановить">
                                <Button
                                    danger
                                    icon={<StopOutlined />}
                                    onClick={() => handleStop(record.id)}
                                />
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title="Получатели">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => viewRecipients(record)}
                        />
                    </Tooltip>
                    {record.status !== 'running' && (
                        <Tooltip title="Удалить">
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(record.id)}
                            />
                        </Tooltip>
                    )}
                </Space>
            )
        }
    ]

    return (
        <div style={{ padding: 24 }}>
            <Card
                title={
                    <span style={{ fontSize: 20 }}>
                        📤 Массовая рассылка
                    </span>
                }
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchBroadcasts}
                        >
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreateModal}
                        >
                            Создать рассылку
                        </Button>
                    </Space>
                }
            >
                <Table
                    dataSource={broadcasts}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Create Modal */}
            <Modal
                title={editingBroadcastId ? "Редактировать рассылку" : "Создать рассылку"}
                open={createModalVisible}
                onCancel={() => {
                    setCreateModalVisible(false)
                    setSelectedDialogs([])
                    form.resetFields()
                    setEditingBroadcastId(null) // Reset editing ID on close
                    setSelectedAccounts([])
                    setParsedFromText([])
                    setTextareaInput('')
                    setCsvData([])
                    setSelectedContacts([])
                    setRecipientTab('chats')
                    setSendMode('single_account')
                }}
                footer={null}
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreate}
                    initialValues={{
                        mode: 'single_account',
                        min_delay_seconds: 30,
                        max_delay_seconds: 60,
                        daily_limit_per_account: 50
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Название кампании"
                        rules={[{ required: true, message: 'Введите название' }]}
                    >
                        <Input placeholder="Например: Новогодняя рассылка" />
                    </Form.Item>

                    <Form.Item
                        name="message_text"
                        label="Текст сообщения"
                        rules={[{ required: true, message: 'Введите текст' }]}
                    >
                        <TextArea rows={4} placeholder="Текст сообщения для рассылки..." />
                    </Form.Item>

                    <Form.Item name="mode" label="Режим отправки">
                        <Select onChange={(value) => setSendMode(value)}>
                            <Option value="single_account">С одного аккаунта</Option>
                            <Option value="multi_account">С нескольких аккаунтов</Option>
                        </Select>
                    </Form.Item>

                    {sendMode === 'single_account' ? (
                        <Form.Item name="default_account_id" label="Аккаунт для отправки">
                            <Select placeholder="Выберите аккаунт" allowClear>
                                {accounts.map(acc => (
                                    <Option key={acc.id} value={acc.id}>{acc.phone}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        <Form.Item label={`Аккаунты для рассылки (${selectedAccounts.length} выбрано)`}>
                            <Select
                                mode="multiple"
                                placeholder="Выберите несколько аккаунтов"
                                value={selectedAccounts}
                                onChange={(values) => setSelectedAccounts(values)}
                                style={{ width: '100%' }}
                            >
                                {accounts.map(acc => (
                                    <Option key={acc.id} value={acc.id}>{acc.phone}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Form.Item
                            name="min_delay_seconds"
                            label="Мин. задержка (сек)"
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={10} max={300} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="max_delay_seconds"
                            label="Макс. задержка (сек)"
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={10} max={300} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="daily_limit_per_account"
                            label="Лимит в день"
                            style={{ flex: 1 }}
                        >
                            <InputNumber min={1} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                    </div>

                    {/* Recipients selection */}
                    <Form.Item label={`Получатели (${getTotalRecipientsCount()} выбрано)`}>
                        <Button
                            icon={<TeamOutlined />}
                            onClick={() => {
                                if (accounts.length > 0) {
                                    loadDialogs(accounts[0].id)
                                    setSelectChatsModalVisible(true)
                                } else {
                                    message.warning('Нет активных аккаунтов')
                                }
                            }}
                            disabled={!!editingBroadcastId} // Disable recipient selection when editing
                        >
                            {editingBroadcastId ? "Получатели (нельзя изменить при редактировании)" : "Выбрать получателей"}
                        </Button>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            disabled={!editingBroadcastId && getTotalRecipientsCount() === 0}
                        >
                            {editingBroadcastId ? "Сохранить изменения" : `Создать рассылку (${getTotalRecipientsCount()} получателей)`}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Select Recipients Modal */}
            <Modal
                title="Выберите получателей"
                open={selectChatsModalVisible}
                onCancel={() => setSelectChatsModalVisible(false)}
                onOk={() => setSelectChatsModalVisible(false)}
                width={700}
                okText={`Готово (${getTotalRecipientsCount()} выбрано)`}
            >
                <Tabs
                    activeKey={recipientTab}
                    onChange={(key) => {
                        setRecipientTab(key)
                        if (key === 'contacts' && contacts.length === 0) {
                            fetchContacts()
                        }
                    }}
                    items={[
                        {
                            key: 'chats',
                            label: <span><UserOutlined /> Из чатов</span>,
                            children: (
                                <div>
                                    <div style={{ marginBottom: 16 }}>
                                        <Select
                                            style={{ width: '60%', marginRight: 8 }}
                                            placeholder="Выберите аккаунт"
                                            onChange={(accountId) => loadDialogs(accountId)}
                                            defaultValue={accounts.length > 0 ? accounts[0].id : undefined}
                                        >
                                            {accounts.map(acc => (
                                                <Option key={acc.id} value={acc.id}>{acc.phone}</Option>
                                            ))}
                                        </Select>
                                        <Input
                                            style={{ width: '38%' }}
                                            placeholder="Поиск..."
                                            prefix={<SearchOutlined />}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            allowClear
                                        />
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <Checkbox
                                            checked={selectedDialogs.length === filteredDialogs.length && filteredDialogs.length > 0}
                                            indeterminate={selectedDialogs.length > 0 && selectedDialogs.length < filteredDialogs.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedDialogs(filteredDialogs.map(d => d.chat_id))
                                                } else {
                                                    setSelectedDialogs([])
                                                }
                                            }}
                                        >
                                            Выбрать всех ({filteredDialogs.length})
                                        </Checkbox>
                                        {selectedDialogs.length > 0 && (
                                            <Tag color="blue" style={{ marginLeft: 8 }}>
                                                Выбрано: {selectedDialogs.length}
                                            </Tag>
                                        )}
                                    </div>

                                    {dialogsLoading ? (
                                        <div style={{ textAlign: 'center', padding: 40 }}>
                                            <Spin size="large" />
                                        </div>
                                    ) : filteredDialogs.length === 0 ? (
                                        <Empty description="Нет диалогов" />
                                    ) : (
                                        <List
                                            style={{ maxHeight: 350, overflow: 'auto' }}
                                            dataSource={filteredDialogs}
                                            renderItem={(dialog) => (
                                                <List.Item style={{ padding: '8px 0' }}>
                                                    <Checkbox
                                                        checked={selectedDialogs.includes(dialog.chat_id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedDialogs([...selectedDialogs, dialog.chat_id])
                                                            } else {
                                                                setSelectedDialogs(selectedDialogs.filter(id => id !== dialog.chat_id))
                                                            }
                                                        }}
                                                    >
                                                        <UserOutlined style={{ marginRight: 8 }} />
                                                        {dialog.first_name || ''} {dialog.last_name || ''}
                                                        {dialog.username && (
                                                            <span style={{ color: '#888', marginLeft: 8 }}>
                                                                @{dialog.username}
                                                            </span>
                                                        )}
                                                    </Checkbox>
                                                </List.Item>
                                            )}
                                        />
                                    )}
                                </div>
                            )
                        },
                        {
                            key: 'text',
                            label: <span><TeamOutlined /> Списком</span>,
                            children: (
                                <div>
                                    <Alert
                                        message="Введите username или ID по одному на строку"
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                    <TextArea
                                        rows={8}
                                        placeholder={`@username1\n@username2\n123456789\nusername3`}
                                        value={textareaInput}
                                        onChange={(e) => {
                                            setTextareaInput(e.target.value)
                                            parseTextInput(e.target.value)
                                        }}
                                    />
                                    {parsedFromText.length > 0 && (
                                        <div style={{ marginTop: 12 }}>
                                            <Tag color="green">Распознано: {parsedFromText.length} получателей</Tag>
                                            <div style={{ marginTop: 8, maxHeight: 150, overflow: 'auto' }}>
                                                {parsedFromText.slice(0, 10).map((p, i) => (
                                                    <Tag key={i} style={{ margin: 4 }}>{p.display}</Tag>
                                                ))}
                                                {parsedFromText.length > 10 && (
                                                    <Tag>...ещё {parsedFromText.length - 10}</Tag>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        },
                        {
                            key: 'csv',
                            label: <span><UploadOutlined /> CSV файл</span>,
                            children: (
                                <div>
                                    <Alert
                                        message="Загрузите CSV с колонками: username, tg_id, phone, name"
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                    <Upload
                                        accept=".csv,.txt"
                                        beforeUpload={handleCsvUpload}
                                        showUploadList={false}
                                    >
                                        <Button icon={<UploadOutlined />}>
                                            Загрузить CSV
                                        </Button>
                                    </Upload>

                                    {csvData.length > 0 && (
                                        <div style={{ marginTop: 16 }}>
                                            <Tag color="green">Загружено: {csvData.length} контактов</Tag>
                                            <Table
                                                size="small"
                                                style={{ marginTop: 12 }}
                                                dataSource={csvData.slice(0, 5)}
                                                rowKey={(_, i) => i!}
                                                pagination={false}
                                                columns={[
                                                    { title: 'Username', dataIndex: 'username', key: 'username' },
                                                    { title: 'TG ID', dataIndex: 'tg_id', key: 'tg_id' },
                                                    { title: 'Телефон', dataIndex: 'phone', key: 'phone' },
                                                    { title: 'Имя', dataIndex: 'name', key: 'name' },
                                                ]}
                                            />
                                            {csvData.length > 5 && (
                                                <div style={{ textAlign: 'center', marginTop: 8, color: '#888' }}>
                                                    ...и ещё {csvData.length - 5} записей
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        },
                        {
                            key: 'contacts',
                            label: <span><TeamOutlined /> Все контакты</span>,
                            children: (
                                <div>
                                    <div style={{ marginBottom: 12 }}>
                                        <Checkbox
                                            checked={selectedContacts.length === contacts.length && contacts.length > 0}
                                            indeterminate={selectedContacts.length > 0 && selectedContacts.length < contacts.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedContacts(contacts.map(c => c.tg_user_id))
                                                } else {
                                                    setSelectedContacts([])
                                                }
                                            }}
                                        >
                                            Выбрать всех ({contacts.length})
                                        </Checkbox>
                                        {selectedContacts.length > 0 && (
                                            <Tag color="blue" style={{ marginLeft: 8 }}>
                                                Выбрано: {selectedContacts.length}
                                            </Tag>
                                        )}
                                    </div>

                                    {contactsLoading ? (
                                        <div style={{ textAlign: 'center', padding: 40 }}>
                                            <Spin size="large" />
                                        </div>
                                    ) : contacts.length === 0 ? (
                                        <Empty description="Нет контактов" />
                                    ) : (
                                        <List
                                            style={{ maxHeight: 350, overflow: 'auto' }}
                                            dataSource={contacts}
                                            renderItem={(contact) => (
                                                <List.Item style={{ padding: '8px 0' }}>
                                                    <Checkbox
                                                        checked={selectedContacts.includes(contact.tg_user_id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedContacts([...selectedContacts, contact.tg_user_id])
                                                            } else {
                                                                setSelectedContacts(selectedContacts.filter(id => id !== contact.tg_user_id))
                                                            }
                                                        }}
                                                    >
                                                        <UserOutlined style={{ marginRight: 8 }} />
                                                        {contact.first_name || ''} {contact.last_name || ''}
                                                        {contact.username && (
                                                            <span style={{ color: '#888', marginLeft: 8 }}>
                                                                @{contact.username}
                                                            </span>
                                                        )}
                                                    </Checkbox>
                                                </List.Item>
                                            )}
                                        />
                                    )}
                                </div>
                            )
                        }
                    ]}
                />
            </Modal>

            {/* Recipients Modal */}
            <Modal
                title={`Получатели: ${selectedBroadcast?.name}`}
                open={recipientsModalVisible}
                onCancel={() => setRecipientsModalVisible(false)}
                footer={null}
                width={700}
            >
                <Table
                    dataSource={recipients}
                    rowKey="id"
                    size="small"
                    columns={[
                        {
                            title: 'Контакт',
                            dataIndex: 'contact_name',
                            key: 'contact_name',
                            render: (name: string, r: Recipient) => {
                                if (name) return name;
                                if (r.username) return `@${r.username}`;
                                return `ID: ${r.tg_chat_id}`;
                            }
                        },
                        {
                            title: 'Статус',
                            dataIndex: 'status',
                            key: 'status',
                            render: (status: string) => {
                                const colors: Record<string, string> = {
                                    pending: 'default',
                                    sending: 'processing',
                                    sent: 'success',
                                    failed: 'error',
                                    skipped: 'warning'
                                }
                                const labels: Record<string, string> = {
                                    pending: 'Ожидает',
                                    sending: 'Отправка',
                                    sent: 'Отправлено',
                                    failed: 'Ошибка',
                                    skipped: 'Пропущен'
                                }
                                return <Tag color={colors[status]}>{labels[status] || status}</Tag>
                            }
                        },
                        {
                            title: 'Ошибка',
                            dataIndex: 'error_message',
                            key: 'error_message',
                            render: (msg: string) => msg ? (
                                <Tooltip title={msg}>
                                    <span style={{ color: '#ff4d4f' }}>
                                        {msg.substring(0, 30)}...
                                    </span>
                                </Tooltip>
                            ) : '-'
                        },
                        {
                            title: 'Отправлено',
                            dataIndex: 'sent_at',
                            key: 'sent_at',
                            render: (date: string) => date ? new Date(date).toLocaleString() : '-'
                        }
                    ]}
                />
            </Modal>
        </div>
    )
}

export default Broadcasts
